import React, { useState, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { useNavigate } from 'react-router-dom';
import { makeRequest2 } from '../../../../../../utils/makeRequest';
import { useFacilityStore } from '../../../../../../app/z-store/store';
import { toastify } from '../../../../../../utils/toast';
import { customer_account_base_url } from '../../../../../../utils/urls';

const CombinedCustomersTab = () => {
    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [balanceLoading, setBalanceLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const navigate = useNavigate();
    const [rows, setRows] = useState(10);
    const selectedFacilityId = useFacilityStore((state) => state.facilityId);

    // Import Modal States
    const [showImportModal, setShowImportModal] = useState(false);
    const [importFile, setImportFile] = useState(null);

    const rowsPerPageOptions = [10, 20, 50, 100];

    useEffect(() => {
        if (selectedFacilityId) {
            fetchAccounts(selectedFacilityId);
        }
    }, [selectedFacilityId]);

    const fetchAccounts = async (facilityId) => {
        try {
            setLoading(true);
            const idToUse = facilityId || selectedFacilityId;
            if (!idToUse) {
                throw new Error('No facility ID available');
            }
            const response = await makeRequest2(
                `${customer_account_base_url}/all/${idToUse}`,
                'GET',
                null
            );
            if (response.success) {
                const accountsData = Array.isArray(response.data.data)
                    ? response.data.data
                    : [];

                const transformedData = accountsData.map((account) => ({
                    id: account._id || account.id,
                    CustomerInfo: {
                        fullName: account.customerName,
                    },
                    meterNumber: account.meterNumber || 'N/A',
                    UnitInfo: { name: account.unitName || 'N/A' },
                    accountNumber: account.account_no,
                    userType: account.payment_type,
                    accountBalance: 0,
                    negativeBalance: 0,
                    currentReading: account.currentReading,
                    status: account.status,
                    originalData: account,
                    balanceLoaded: false
                }));
                setAccounts(transformedData);
                await fetchBalances(transformedData);
            } else {
                toastify('Failed to fetch customers', 'error');
            }
        } catch (error) {
            toastify('Error fetching customers', 'error');
        } finally {
            setLoading(false);
        }
    };

    const fetchBalances = async (accountsList) => {
        try {
            setBalanceLoading(true);
            const balancePromises = accountsList.map(async (account) => {
                try {
                    const response = await makeRequest2(
                        `${customer_account_base_url}/get_accounts_balance/${account.id}`,
                        'GET',
                        null
                    );

                    if (response.success) {
                        const balanceData = response.data.data || {};
                        return {
                            id: account.id,
                            accountBalance: balanceData.currentBalance || 0,
                            negativeBalance: balanceData.negativeBalance || 0,
                            balanceLoaded: true
                        };
                    } else {
                        return {
                            id: account.id,
                            accountBalance: 0,
                            negativeBalance: 0,
                            balanceLoaded: true
                        };
                    }
                } catch (error) {
                    console.error(`Error fetching balance for account ${account.id}:`, error);
                    return {
                        id: account.id,
                        accountBalance: 0,
                        negativeBalance: 0,
                        balanceLoaded: true
                    };
                }
            });

            const balanceResults = await Promise.all(balancePromises);
            setAccounts(prevAccounts =>
                prevAccounts.map(account => {
                    const balanceData = balanceResults.find(b => b.id === account.id);
                    if (balanceData) {
                        return {
                            ...account,
                            accountBalance: balanceData.accountBalance,
                            negativeBalance: balanceData.negativeBalance,
                            balanceLoaded: balanceData.balanceLoaded
                        };
                    }
                    return account;
                })
            );
        } catch (error) {
            console.error('Error fetching balances:', error);
            toastify('Error fetching account balances', 'error');
        } finally {
            setBalanceLoading(false);
        }
    };

    const handleView = (rowData) => {
        const accountId = rowData.id || rowData._id;
        const paymentType = rowData.userType?.toLowerCase();

        if (paymentType === 'prepaid') {
            navigate(
                `/facility/utility_management/meter_management/water_meters/view_meter_tabs/customer_account/view_customer_details1/${accountId}`,
                {
                    state: { accountId: accountId }
                }
            );
        } else if (paymentType === 'postpaid') {
            navigate(
                `/facility/utility_management/meter_management/water_meters/view_customer_account`,
                {
                    state: { accountId: accountId }
                }
            );
        } else {
            toastify('Unable to determine payment type for navigation', 'warning');
            console.warn('Unknown payment type:', rowData.userType);
        }
    };

    const actionBodyTemplate = (rowData) => (
        <div className="flex gap-2">
            <Button
                icon="ti ti-eye"
                className="p-button-rounded p-button-text"
                onClick={() => handleView(rowData)}
                tooltip="View"
            />
        </div>
    );

    const statusBodyTemplate = (rowData) => {
        const statusColors = {
            active: 'success',
            inactive: 'danger'
        };

        const status = rowData.status ? rowData.status.toLowerCase() : 'n/a';
        const color = statusColors[status] || 'secondary';

        return (
            <span className={`badge rounded-pill text-bg-${color}`}>
                {rowData.status
                    ? rowData.status.charAt(0).toUpperCase() + rowData.status.slice(1)
                    : 'N/A'}
            </span>
        );
    };

    const paymentTypeTemplate = (rowData) => {
        const typeColors = {
            postpaid: 'warning',
            prepaid: 'success'
        };
        const userType = rowData.userType ? rowData.userType.toLowerCase() : 'n/a';
        const color = typeColors[userType] || 'secondary';

        return (
            <span className={`badge rounded-pill text-bg-${color}`}>
                {rowData.userType
                    ? rowData.userType.charAt(0).toUpperCase() + rowData.userType.slice(1)
                    : 'N/A'}
            </span>
        );
    };

    const currencyTemplate = (value, isLoading = false) => {
        if (isLoading) {
            return (
                <div className="d-flex align-items-center">
                    <div className="spinner-border spinner-border-sm text-primary me-2" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <span>Loading...</span>
                </div>
            );
        }

        return value
            ? new Intl.NumberFormat('en-KE', {
                style: 'currency',
                currency: 'KES'
            }).format(value)
            : 'KES 0.00';
    };

    const accountBalanceTemplate = (rowData) => {
        return currencyTemplate(rowData.accountBalance, balanceLoading && !rowData.balanceLoaded);
    };

    const negativeBalanceTemplate = (rowData) => {
        return currencyTemplate(rowData.negativeBalance, balanceLoading && !rowData.balanceLoaded);
    };

    const refreshBalances = async () => {
        if (accounts.length > 0) {
            await fetchBalances(accounts);
            toastify('Balances refreshed successfully', 'success');
        }
    };

    const filteredAccounts = accounts.filter((account) => {
        const searchLower = searchTerm.toLowerCase();
        const searchableFields = [
            account.CustomerInfo?.fullName,
            account.accountNumber,
            account.UnitInfo?.name,
            account.meterNumber
        ];
        return searchableFields.some(field =>
            field?.toString().toLowerCase().includes(searchLower)
        );
    });

    // ===== IMPORT FUNCTIONALITY =====
    const handleImportModalOpen = () => {
        setShowImportModal(true);
    };

    const handleImportModalClose = () => {
        setShowImportModal(false);
        setImportFile(null);
    };

    const generateCSVTemplate = () => {
        let csvContent = "meterNumber,customerName,phoneNumber,payment_type,email\n";
        csvContent += "MTR001,John Doe,0712345678,Prepaid,johndo@gmail.com\n";
        csvContent += "MTR002,Jane Smith,0723456789,Postpaid,janesmith@gmail.com\n";
        csvContent += "MTR003,Peter Kimani,0734567890,Prepaid,peterkimani@gmail.com\n";
        return csvContent;
    };

    const handleTemplateDownload = () => {
        const csvContent = generateCSVTemplate();
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `customer_accounts_template.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            setImportFile(e.target.files[0]);
        }
    };

    const handleImportSubmit = async (e) => {
        e.preventDefault();

        if (!importFile) {
            toastify('Please select a CSV file', 'error');
            return;
        }

        try {
            setLoading(true);
            const formData = new FormData();
            formData.append('file', importFile);

            const response = await makeRequest2(
                `${customer_account_base_url}/import/${selectedFacilityId}`,
                'POST',
                formData
            );

            if (response.success) {
                toastify('Customer accounts imported successfully', 'success');
                fetchAccounts(selectedFacilityId);
                setShowImportModal(false);
                setImportFile(null);
            } else {
                toastify(response.error || 'Failed to import customer accounts', 'error');
            }
        } catch (error) {
            toastify('Error importing CSV', 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <div className="card">
                <div className="card-body">
                    <div className="row mb-3">
                        <div className="col-md-3 col-xs-12">
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Search customers..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <div className="col-md-9 col-xs-12 d-flex justify-content-end gap-2">
                            <button
                                className="btn btn-primary"
                                onClick={handleImportModalOpen}
                            >
                                Import Customers
                            </button>
                            <button
                                className="btn btn-outline-primary"
                                onClick={refreshBalances}
                                disabled={balanceLoading}
                            >
                                <i className="ti ti-refresh me-1"></i>
                                {balanceLoading ? 'Refreshing...' : 'Refresh Balances'}
                            </button>
                        </div>
                    </div>
                    <div className="dt-responsive table-responsive">
                        <DataTable
                            value={filteredAccounts}
                            emptyMessage="No customers found."
                            sortMode="multiple"
                            paginator
                            rows={rows}
                            rowsPerPageOptions={rowsPerPageOptions}
                            stripedRows
                            loading={loading}
                            tableStyle={{ minWidth: '50rem' }}
                        >
                            <Column
                                field="fullname"
                                header="Customer"
                                sortable
                                body={(rowData) =>
                                    rowData.CustomerInfo ? rowData.CustomerInfo.fullName : 'N/A'
                                }
                            />
                            <Column field="meterNumber" header="Meter S/N" sortable />
                            <Column
                                field="UnitInfo"
                                header="Unit"
                                sortable
                                body={(rowData) =>
                                    rowData.UnitInfo ? rowData.UnitInfo.name : 'N/A'
                                }
                            />
                            <Column field="accountNumber" header="Account No" sortable />
                            <Column field="userType" header="Payment Type" sortable body={paymentTypeTemplate} />
                            <Column
                                field="accountBalance"
                                header="Account Bal"
                                sortable
                                body={accountBalanceTemplate}
                            />
                            <Column
                                field="negativeBalance"
                                header="Negative Bal"
                                sortable
                                body={negativeBalanceTemplate}
                            />
                            <Column
                                field="status"
                                header="Status"
                                body={statusBodyTemplate}
                                sortable
                            />
                            <Column header="Actions" body={actionBodyTemplate} />
                        </DataTable>
                    </div>
                </div>
            </div>

            {/* Import Customer Accounts Modal */}
            {showImportModal && (
                <div
                    className="modal d-block"
                    tabIndex="-1"
                    style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
                >
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Import Customer Accounts</h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={handleImportModalClose}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <p>
                                    <button
                                        type="button"
                                        className="btn btn-link p-0"
                                        onClick={handleTemplateDownload}
                                    >
                                        Download customer accounts template (.csv)
                                    </button>
                                </p>
                                <div className="alert alert-info">
                                    <strong>Template Info</strong>
                                    <ul className="mb-0">
                                        <li><strong>meterNumber:</strong> Unique meter serial number (Required)</li>
                                        <li><strong>customerName:</strong> Full name of customer (Required)</li>
                                        <li><strong>phoneNumber:</strong> Phone number format 07XXXXXXXX or 01XXXXXXXX (Required)</li>
                                        <li><strong>payment_type:</strong> Either "Prepaid" or "Postpaid" (Required)</li>
                                        <li><strong>email:</strong> Email address (optional)</li>
                                    </ul>
                                </div>

                                <div>
                                    <div className="mb-3">
                                        <label className="form-label">Upload Completed CSV File</label>
                                        <input
                                            type="file"
                                            accept=".csv"
                                            className="form-control"
                                            onChange={handleFileChange}
                                        />
                                    </div>
                                    <div className="text-end">
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary me-2"
                                            onClick={handleImportModalClose}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-primary"
                                            onClick={handleImportSubmit}
                                            disabled={loading || !importFile}
                                        >
                                            {loading ? (
                                                <span className="spinner-border spinner-border-sm me-1"></span>
                                            ) : null}
                                            Submit
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default CombinedCustomersTab;